import React from "react";
import {
  Easing,
  interpolate,
  interpolateColors,
  useCurrentFrame,
} from "remotion";
import { FrakLogo } from "../../components/FrakLogo";
import { Tap } from "../../b2c/ui/bits";
import { useCopy } from "../copy";
import { LOOK } from "../look";
import { useStage } from "../stage";
import { Headline, Kicker, Split, splitGeom, Sub } from "../ui/Split";
import { Sfx } from "../ui/Sfx";
import { usePop, Window } from "../ui/Surfaces";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/**
 * Step 1 — set your acquisition cost, upfront. The partner page's
 * campaign screen, coded: pick the goal, type the CAC (10 €, the landing's
 * own example), and watch it split 8 € to the ambassador / 2 € to Frak
 * (the 20 % commission). The budget line closes the maths: 1 000 € =
 * 100 new customers, never more per sale.
 */

const VISUAL = { w: 840, h: 680 };
const CAC = 10;
/**
 * UI pacing in frames on purpose (how fast a hand taps and types), not
 * beats: only the scene length follows the music.
 */
const TAP_GOAL = 18;
const TYPE_FROM = 30;
const SPLIT_AT = 46;
const BUDGET_AT = 66;
/** Tap position inside the visual (the "Ventes" pill). */
const GOAL_PILL = { x: 116, y: 254 };

const Field: React.FC<{
  label: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ label, children, style }) => (
  <div style={style}>
    <div
      style={{
        fontFamily: LOOK.body,
        fontWeight: LOOK.semi,
        fontSize: 26,
        color: LOOK.inkSoft,
        marginBottom: 12,
      }}
    >
      {label}
    </div>
    {children}
  </div>
);

const Dashboard: React.FC = () => {
  const frame = useCurrentFrame();
  const copy = useCopy();
  const c = copy.setCac;

  const selected = interpolate(frame, [TAP_GOAL, TAP_GOAL + 4], [0, 1], clamp);
  // "1" then "10", like someone typing.
  const typed =
    frame < TYPE_FROM ? "" : frame < TYPE_FROM + 5 ? "1" : String(CAC);
  const caret =
    frame >= TYPE_FROM - 6 &&
    frame < SPLIT_AT &&
    Math.floor(frame / 8) % 2 === 0;
  const split = interpolate(frame, [SPLIT_AT, SPLIT_AT + 16], [0, 1], {
    ...clamp,
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  const labels = usePop(SPLIT_AT + 10);
  const budget = usePop(BUDGET_AT, true);

  return (
    <Window
      tag={copy.sample}
      style={{ width: VISUAL.w, height: VISUAL.h }}
      bodyStyle={{ padding: "40px 48px" }}
    >
      <div
        style={{
          fontFamily: LOOK.display,
          fontWeight: LOOK.bold,
          fontSize: 40,
          letterSpacing: -1,
          color: LOOK.ink,
          marginBottom: 30,
        }}
      >
        {c.title}
      </div>

      <Field label={c.goal}>
        <div style={{ display: "flex", gap: 14 }}>
          {c.goals.map((g, i) => {
            const on = i === 0 ? selected : 0;
            return (
              <div
                key={g}
                style={{
                  padding: "12px 28px",
                  borderRadius: 999,
                  fontFamily: LOOK.body,
                  fontWeight: LOOK.semi,
                  fontSize: 28,
                  background: interpolateColors(
                    on,
                    [0, 1],
                    [LOOK.mist, LOOK.blue],
                  ),
                  color: interpolateColors(
                    on,
                    [0, 1],
                    [LOOK.inkSoft, LOOK.white],
                  ),
                }}
              >
                {g}
              </div>
            );
          })}
        </div>
      </Field>

      <Field label={c.cacLabel} style={{ marginTop: 30 }}>
        <div
          style={{
            height: 92,
            borderRadius: 24,
            border: `3px solid ${frame >= TYPE_FROM - 6 ? LOOK.blue : LOOK.hairline}`,
            display: "flex",
            alignItems: "center",
            padding: "0 30px",
            fontFamily: LOOK.display,
            fontWeight: LOOK.bold,
            fontSize: 52,
            color: LOOK.ink,
          }}
        >
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{typed}</span>
          <span
            style={{
              width: 4,
              height: 52,
              marginLeft: 4,
              background: LOOK.blue,
              opacity: caret ? 1 : 0,
            }}
          />
          <span
            style={{
              marginLeft: "auto",
              fontFamily: LOOK.body,
              fontWeight: LOOK.semi,
              fontSize: 30,
              color: LOOK.muted,
            }}
          >
            EUR
          </span>
        </div>
      </Field>

      {/* The split: 80 % to the ambassador, 20 % to Frak. */}
      <div style={{ marginTop: 30 }}>
        <div
          style={{
            display: "flex",
            height: 30,
            borderRadius: 30,
            overflow: "hidden",
            background: LOOK.mist,
          }}
        >
          <div style={{ width: `${80 * split}%`, background: LOOK.green }} />
          <div
            style={{
              width: `${20 * split}%`,
              background: LOOK.blue,
              marginLeft: 4 * split,
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 16,
            opacity: labels,
            translate: `0 ${(1 - labels) * 16}px`,
          }}
        >
          <SplitLabel
            dot={LOOK.green}
            label={c.ambassadors}
            value={copy.money(CAC * 0.8)}
          />
          <SplitLabel
            dot={LOOK.blue}
            label={c.frak}
            value={copy.money(CAC * 0.2)}
            icon={<FrakLogo variant="icon" width={26} />}
            right
          />
        </div>
      </div>

      {/* Budget → customers. */}
      <div
        style={{
          position: "absolute",
          left: 48,
          right: 48,
          bottom: 36,
          padding: "20px 28px",
          borderRadius: 24,
          background: LOOK.mist,
          display: "flex",
          alignItems: "center",
          gap: 18,
          fontFamily: LOOK.body,
          fontSize: 30,
          color: LOOK.ink,
          opacity: budget,
          translate: `0 ${(1 - budget) * 30}px`,
        }}
      >
        <span style={{ fontWeight: LOOK.medium, color: LOOK.inkSoft }}>
          {c.budget}
        </span>
        <span style={{ fontWeight: LOOK.bold }}>{copy.money(1000)}</span>
        <span style={{ color: LOOK.muted }}>→</span>
        <span style={{ fontWeight: LOOK.bold, color: LOOK.blue }}>
          {c.budgetResult}
        </span>
      </div>
    </Window>
  );
};

const SplitLabel: React.FC<{
  dot: string;
  label: string;
  value: string;
  icon?: React.ReactNode;
  right?: boolean;
}> = ({ dot, label, value, icon, right }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexDirection: right ? "row-reverse" : "row",
      fontFamily: LOOK.body,
    }}
  >
    {icon ?? (
      <div
        style={{ width: 18, height: 18, borderRadius: 18, background: dot }}
      />
    )}
    <span
      style={{ fontWeight: LOOK.medium, fontSize: 28, color: LOOK.inkSoft }}
    >
      {label}
    </span>
    <span
      style={{
        fontFamily: LOOK.display,
        fontWeight: LOOK.bold,
        fontSize: 40,
        letterSpacing: -1,
        color: dot === LOOK.green ? LOOK.green : LOOK.blue,
      }}
    >
      {value}
    </span>
  </div>
);

export const SetCac: React.FC = () => {
  const copy = useCopy();
  const stage = useStage();
  // Square: the text needs less room than tall's 4:5 frame gives it, and
  // every px goes to the dashboard (it read at ~0.75x with tall's value).
  const textH = { tall: 380, square: 320, wide: 390 }[stage.shape];
  const g = splitGeom(stage, VISUAL, textH);
  const origin = {
    x: g.visual.x + g.visual.w / 2 - (VISUAL.w / 2) * g.scale,
    y: g.visual.y + g.visual.h / 2 - (VISUAL.h / 2) * g.scale,
  };
  return (
    <>
      <Split
        background={LOOK.sky}
        visualSize={VISUAL}
        textH={textH}
        text={
          <>
            <Kicker text={copy.setCac.kicker} step={1} />
            <Headline text={copy.setCac.headline} start={2} />
            <Sub text={copy.setCac.sub} at={SPLIT_AT - 6} />
          </>
        }
        visual={<Dashboard />}
      >
        <Tap
          x={origin.x + GOAL_PILL.x * g.scale}
          y={origin.y + GOAL_PILL.y * g.scale}
          at={TAP_GOAL}
          size={70}
        />
      </Split>
      <Sfx sound="click" at={TAP_GOAL} />
      {/* "1", then "0" (see `typed`). */}
      <Sfx sound="key" at={TYPE_FROM} />
      <Sfx sound="key" at={TYPE_FROM + 5} />
      <Sfx sound="slide" at={SPLIT_AT} volume={0.8} />
      <Sfx sound="pop" at={BUDGET_AT} />
    </>
  );
};
